import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";

export type AuthSecurityEvent =
  | "passkey_enrolled"
  | "passkey_removed"
  | "passkey_login_success"
  | "passkey_login_failure"
  | "session_restored"
  | "session_restore_failed"
  | "logout";

export interface BrowserWebAuthnCapability {
  supported: boolean;
  platformAuthenticatorAvailable: boolean;
  reason: string;
}

export interface SecurityContext {
  passkeyEnabled: boolean;
  biometricUnlockEnabled: boolean;
  rememberDevice: boolean;
  passkeyProjectSupported: boolean;
  passkeyLastUpdatedAt: string | null;
  raw: Record<string, unknown>;
}

export const DEFAULT_SECURITY_CONTEXT: SecurityContext = {
  passkeyEnabled: false,
  biometricUnlockEnabled: false,
  rememberDevice: false,
  passkeyProjectSupported: false,
  passkeyLastUpdatedAt: null,
  raw: {},
};

export function normalizeSecurityContext(payload: unknown): SecurityContext {
  const record = (payload && typeof payload === "object" ? payload : {}) as Record<string, unknown>;

  const passkeyEnabled =
    typeof record.passkey_enabled === "boolean"
      ? record.passkey_enabled
      : typeof record.passkeys_enabled === "boolean"
        ? record.passkeys_enabled
        : false;

  const passkeyProjectSupported =
    typeof record.passkey_project_supported === "boolean"
      ? record.passkey_project_supported
      : typeof record.webauthn_enabled === "boolean"
        ? record.webauthn_enabled
        : typeof record.passkey_supported === "boolean"
          ? record.passkey_supported
          : false;

  const biometricUnlockEnabled = typeof record.biometric_unlock_enabled === "boolean"
    ? record.biometric_unlock_enabled
    : false;

  const rememberDevice = typeof record.remember_device === "boolean"
    ? record.remember_device
    : false;

  const passkeyLastUpdatedAt =
    typeof record.passkey_last_updated_at === "string"
      ? record.passkey_last_updated_at
      : typeof record.updated_at === "string"
        ? record.updated_at
        : null;

  return {
    passkeyEnabled,
    biometricUnlockEnabled,
    rememberDevice,
    passkeyProjectSupported,
    passkeyLastUpdatedAt,
    raw: record,
  };
}

export async function detectBrowserWebAuthnCapability(): Promise<BrowserWebAuthnCapability> {
  if (typeof window === "undefined") {
    return {
      supported: false,
      platformAuthenticatorAvailable: false,
      reason: "WebAuthn is unavailable in server rendering context",
    };
  }

  if (typeof window.PublicKeyCredential === "undefined") {
    return {
      supported: false,
      platformAuthenticatorAvailable: false,
      reason: "This browser does not expose PublicKeyCredential",
    };
  }

  const canCheckPlatform =
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === "function";

  if (!canCheckPlatform) {
    return {
      supported: true,
      platformAuthenticatorAvailable: false,
      reason: "Browser supports WebAuthn but cannot verify platform authenticator availability",
    };
  }

  try {
    const platformAuthenticatorAvailable =
      await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();

    return {
      supported: true,
      platformAuthenticatorAvailable,
      reason: platformAuthenticatorAvailable
        ? "WebAuthn and a platform authenticator are available"
        : "WebAuthn is available but no platform authenticator is currently available",
    };
  } catch {
    return {
      supported: true,
      platformAuthenticatorAvailable: false,
      reason: "WebAuthn check failed unexpectedly in this browser context",
    };
  }
}

export async function ensureUserSecuritySettings(): Promise<void> {
  const { error } = await supabase.rpc("ensure_user_security_settings");
  if (error) {
    throw new Error(error.message || "Failed to initialize user security settings");
  }
}

export async function getAuthSecurityContext(): Promise<SecurityContext> {
  const { data, error } = await supabase.rpc("get_auth_security_context");
  if (error) throw new Error(error.message || "Failed to fetch auth security context");

  const contextRow = Array.isArray(data) ? data[0] : data;
  return normalizeSecurityContext(contextRow);
}

export async function updateUserSecuritySettings(payload: {
  passkeyEnabled: boolean;
  biometricUnlockEnabled: boolean;
  rememberDevice: boolean;
}): Promise<void> {
  const { error } = await supabase.rpc("update_user_security_settings", {
    p_passkey_enabled: payload.passkeyEnabled,
    p_biometric_unlock_enabled: payload.biometricUnlockEnabled,
    p_remember_device: payload.rememberDevice,
  });

  if (error) throw new Error(error.message || "Failed to update user security settings");
}

export async function logAuthSecurityEvent(
  eventType: AuthSecurityEvent,
  metadata: Record<string, unknown> = {},
): Promise<void> {
  const { error } = await supabase.rpc("log_auth_security_event", {
    p_event_type: eventType,
    p_event_detail: metadata as Json,
  });

  if (error) {
    console.warn("[AUTH_SECURITY] Failed to log security event", { eventType, error });
  }
}
